<template>
  <article>
    <activity-stage
      :customRootContainerClass="'on-the-stage'"
      :customScrollContainerStyles="{ background: '#000000' }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div id="curtain" :style="stageStyle">
          <div id="stage">
            <figure v-for="(musician, index) in currentRound?.musicians" :key="`musician${index}`">
              <figcaption v-show="musician.name" v-text="musician.name"></figcaption>
              <img
                :src="musician.colorImage?.image?.src"
                :alt="musician.colorImage?.image?.description"
              />
            </figure>
            <figure>
              <figcaption aria-hidden="true" id="primary">Primary</figcaption>
              <img
                id="color"
                v-if="completed && currentRound?.colorImage"
                :src="currentRound?.colorImage?.image?.src"
                :alt="currentRound?.colorImage?.image?.description"
              />
              <img
                id="bw"
                v-if="!completed && currentRound?.monochromeImage"
                :src="currentRound?.monochromeImage?.image?.src"
                :alt="currentRound?.monochromeImage?.image?.description"
              />
            </figure>
            <div id="dialog">
              <text-display-list element="div" id="input" text-size="small">
                <blinking-cursor-text
                  v-for="(step, stepIndex) in currentRound?.steps"
                  :key="`step${stepIndex}`"
                  :text="step.answer"
                  :text-step="stepIndex"
                ></blinking-cursor-text>
              </text-display-list>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import stage from 'assets/images/activities/musicians-on-stage/stage.png';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  data() {
    return {
      activityName: 'MusiciansOnStage',
      sfx: null,
      stageStyle: {
        backgroundImage: `url(${stage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left bottom',
        backgroundSize: 'cover'
      },
      completed: false
    };
  },

  methods: {
    async loadActivity() {
      this.createRounds({ instructionsClass: 'on-the-stage-instructions' });
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.queueImage(this.stage);

      this.rounds.forEach(r => {
        this.queueImage(r.colorImage?.image?.src);
        this.queueImage(r.monochromeImage?.image?.src);
        r.musicians.forEach(m => {
          this.queueImage(m.colorImage?.image?.src);
        });
      });

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ musicianAudio }) => {
        if (musicianAudio) this.queueAudio(musicianAudio?.audio?.src);
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(({ musicianAudio }) => cache.get(musicianAudio?.audio?.src));
    },

    getKeyboardConfig() {
      const { keyboardType, roofs, showNumbers } = this.activityData.keyboard;
      return {
        keyboardType,
        hideNumberRow: !showNumbers,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.setCurtainHeight();
      });

      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      const { roundIndex } = this.progress;
      const music = this.sfx[roundIndex];

      this.sndBell();
      this.completed = true;
      this.hideKeyboard();

      timeline.delay(0.8).call(() => {
        if (this.isLastRound) {
          music.play();
          music.on('end', () => {
            TweenMax.delayedCall(1.5, this.completeActivity);
          });
        } else {
          timeline
            .delay(2.5)
            .to('.activity-stage', 0.5, { opacity: 0 })
            .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
        }
      });
    },

    setCurtainHeight() {
      const activityStageEl = document.querySelector('.activity-stage');
      const curtainContainerEl = document.querySelector('#curtain');

      if (activityStageEl && curtainContainerEl) {
        curtainContainerEl.style.height = `${activityStageEl.clientHeight - 45}px`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.on-the-stage {
  background-color: black;
  color: #fff;

  #curtain {
    height: 100%;
    overflow: hidden;
    width: 1024px;

    #stage {
      font-family: hwtfutura;
      font-weight: 400;
      font-style: normal;
      position: relative;
      display: inline-flex;
      margin: 6% 0 0 12%;

      figure {
        margin: 0;
        text-align: center;

        #primary {
          margin-top: 2%;
          color: transparent;
        }
      }

      #dialog {
        display: block;
        width: 260px;

        #input {
          position: relative;
          margin: 30% 0 0 8%;
          padding: 3.5%;
          background-color: #fff;
          line-height: 2;
          border-radius: 10px;

          &:before {
            content: '';
            position: absolute;
            display: block;
            margin: 10% 0 0 -20%;
            border-style: solid;
            border-width: 15px 50px 15px 0;
            border-color: transparent #fff;
          }
        }
      }
    }
  }
}

.chrome-os,
.tablet {
  #curtain {
    #stage {
      height: 100%;
      margin: 0px 0px 0px 12%;

      figure {
        height: 100%;

        img {
          height: 90%;
          width: auto;
        }
      }
    }
  }
}

.tablet {
  #curtain {
    width: 100%;
  }
}
</style>
